import type { ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';

interface FloatingActionBarProps {
  /** Quantidade selecionada; a barra aparece quando > 0. */
  count: number;
  onClear: () => void;
  children: ReactNode;
  label?: (n: number) => string;
}

/** Barra de ações em lote, flutuante no rodapé central da tela. */
export function FloatingActionBar({ count, onClear, children, label }: FloatingActionBarProps) {
  const text = label ? label(count) : `${count} selecionado${count > 1 ? 's' : ''}`;
  return (
    <AnimatePresence>
      {count > 0 ? (
        <motion.div
          initial={{ y: 90, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 90, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 420, damping: 34 }}
          className="pointer-events-none fixed inset-x-0 bottom-[max(1.5rem,env(safe-area-inset-bottom))] z-40 flex justify-center px-4"
        >
          <div className="glass-elevated pointer-events-auto flex max-w-[min(100%,42rem)] flex-wrap items-center justify-center gap-1.5 rounded-2xl py-2 pl-4 pr-2 shadow-glass-lg">
            <span className="whitespace-nowrap text-sm font-medium tabular-nums">{text}</span>
            <span className="mx-1.5 h-6 w-px bg-border" />
            <div className="flex items-center gap-1.5">{children}</div>
            <button
              type="button"
              onClick={onClear}
              aria-label="Limpar seleção"
              className="ml-1 grid size-8 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
